import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Activity, Loader2, Radio, Wifi } from "lucide-react";
import { api } from "@/lib/api";
import { DataTable, type Column } from "@/components/DataTable";

export const Route = createFileRoute("/admin/administrateur/integration/observations/")({
  component: Page,
});

interface Observation {
  id: number;
  patient_id: number;
  device_id: string | null;
  loinc_code: string | null;
  value: number | null;
  unit: string | null;
  abnormal_flag: string | null;
  observed_at: string;
}

function Page() {
  const [data, setData] = useState<Observation[]>([]);
  const [loading, setLoading] = useState(true);
  const [liveCount, setLiveCount] = useState<number>(0);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const reload = async () => {
    try {
      const [r, c] = await Promise.all([api.observationsRecent({ limit: 200 }), api.observationsLiveCount()]);
      setData((r as any).items);
      setLiveCount((c as any).active_devices_5min);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    reload();
    if (!autoRefresh) return;
    const id = setInterval(reload, 5000);
    return () => clearInterval(id);
  }, [autoRefresh]);

  const columns: Column<Observation>[] = [
    { key: "id", label: "#", width: "60px" },
    { key: "patient_id", label: "Patient", render: (r) => `P${String(r.patient_id).padStart(4, "0")}` },
    { key: "device_id", label: "Device", render: (r) => r.device_id ? <span className="font-mono text-xs">{r.device_id}</span> : "—" },
    { key: "loinc_code", label: "LOINC", render: (r) => <code className="text-[11px] bg-black/30 px-1 py-0.5 rounded">{r.loinc_code}</code> },
    { key: "value", label: "Valeur", sortable: true, render: (r) => (
      <span className={`font-mono ${r.abnormal_flag ? "text-red-400 font-bold" : ""}`}>{r.value ?? "—"} {r.unit}</span>
    )},
    { key: "abnormal_flag", label: "Flag", render: (r) => r.abnormal_flag ? <Badge variant="destructive">⚠ {r.abnormal_flag}</Badge> : <Badge variant="outline">N</Badge> },
    { key: "observed_at", label: "Horodatage", render: (r) => {
      const d = new Date(r.observed_at);
      const ago = Math.floor((Date.now() - d.getTime()) / 60_000);
      return <span className="text-xs">{d.toLocaleTimeString("fr-FR")} <span className="text-muted-foreground">({ago < 1 ? "maintenant" : `${ago}min`})</span></span>;
    }},
  ];

  const abnormalCount = data.filter((r) => r.abnormal_flag).length;

  return (
    <div className="space-y-4 p-6">
      <Link to="/admin/administrateur" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-3 w-3" /> Dashboard Administrateur
      </Link>
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Radio className="h-6 w-6" /> Observations live
          {autoRefresh && <Badge variant="default" className="ml-2 gap-1"><span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />LIVE</Badge>}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">Stream IoMT temps réel · HL7 / IEEE 11073 / ASTM</p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Card><CardContent className="p-4"><div className="text-xs uppercase text-muted-foreground">Devices live (5 min)</div><div className="mt-1 text-2xl font-bold text-emerald-400 flex items-center gap-2"><Wifi className="h-5 w-5" />{liveCount}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs uppercase text-muted-foreground">Observations totales</div><div className="mt-1 text-2xl font-bold">{data.length}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs uppercase text-muted-foreground">Anomalies</div><div className="mt-1 text-2xl font-bold text-red-400">{abnormalCount}</div></CardContent></Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Auto-refresh</div>
            <Button variant={autoRefresh ? "default" : "outline"} size="sm" className="mt-2" onClick={() => setAutoRefresh(!autoRefresh)}>
              {autoRefresh ? "ON (5s)" : "OFF"}
            </Button>
          </CardContent>
        </Card>
      </div>

      <DataTable
        title="Stream IoMT"
        subtitle="Auto-refresh 5s · données live du middleware"
        data={data}
        columns={columns}
        loading={loading}
        searchFields={["device_id", "patient_id", "loinc_code"]}
        pageSize={15}
      />
    </div>
  );
}